/**
 * BackupSection — Batch #14.338 : sauvegarde automatique complète vers Google Drive.
 * État (prochaine / dernière), planification + mot de passe, « Sauvegarder maintenant », sauvegardes du Drive
 * (télécharger / restaurer), historique, ancien export JSON replié.
 * Batch #14.339 — alertes (échec / manquée) courriel + SMS, rappel mensuel de copie hors ligne (bandeau + « J'ai fait ma copie »),
 * sauvegarde / restauration par dossier client.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { CloudUpload, Database, ExternalLink, HardDrive, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import BackupScheduleForm from "./backup/BackupScheduleForm";
import BackupAlertsForm from "./backup/BackupAlertsForm";
import DossierBackup from "./backup/DossierBackup";
import BackupDriveList from "./backup/BackupDriveList";
import BackupRuns from "./backup/BackupRuns";
import LegacyJsonBackup from "./backup/LegacyJsonBackup";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function BackupSection() {
  const [settings, setSettings] = useState(null);
  const [drive, setDrive] = useState(null);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async (withDrive = true) => {
    try {
      const [s, r] = await Promise.all([api.get("/admin/backup/settings"), api.get("/admin/backup/runs?limit=20")]);
      setSettings(s.data); setRuns(r.data.items || []);
      if (withDrive) {
        try { const d = await api.get("/admin/backup/drive"); setDrive(d.data); }
        catch (e) { setDrive({ items: [], error: e.response?.data?.detail || "Drive injoignable" }); }
      }
      return s.data;
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de chargement"); return null; }
  }, []);

  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, [load]);

  const watch = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const s = await load(false);
      if (s && !s.running) { clearInterval(pollRef.current); load(true); toast.success(s.last_status === "ok" ? "Sauvegarde terminée et envoyée dans le Drive" : "Sauvegarde terminée avec erreur — voir l'historique"); }
    }, 3000);
  };

  const runNow = async () => {
    setBusy(true);
    try { await api.post("/admin/backup/run"); toast.info("Sauvegarde lancée…"); setSettings((s) => ({ ...s, running: true })); watch(); }
    catch (e) { toast.error(e.response?.data?.detail || "Impossible de lancer la sauvegarde"); }
    finally { setBusy(false); }
  };

  const offlineDone = async () => {
    try { await api.post("/admin/backup/offline-done"); toast.success("Copie hors ligne notée — prochain rappel le mois prochain"); load(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Impossible d'enregistrer"); }
  };
  const latest = drive?.items?.find((i) => i.kind !== "prerestore") || drive?.items?.[0];
  const downloadLatest = async () => {
    if (!latest) return;
    try {
      const { getApiBase, authToken } = await import("@/api");
      const resp = await fetch(`${getApiBase()}/admin/backup/drive/${latest.file_id}/download`, { headers: { Authorization: `Bearer ${authToken()}` } });
      if (!resp.ok) throw new Error("Téléchargement impossible");
      const url = URL.createObjectURL(await resp.blob());
      const a = document.createElement("a"); a.href = url; a.download = latest.name; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  if (!settings) return <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>;
  const ok = settings.last_status === "ok";

  return (
    <div className="space-y-6" data-testid="backup-section">
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-1 flex items-center gap-2"><Database className="w-5 h-5 text-indigo-600" /> Sauvegarde automatique</h2>
        <p className="text-sm text-gray-500">Copie complète de toutes vos données (comptabilité, paie, dossiers, fiscal, fichiers) dans une archive <b>chiffrée</b> envoyée dans votre Google Drive, selon l'horaire que vous choisissez. Rétention : {settings.retention_daily} quotidiennes + {settings.retention_monthly} mensuelles.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="backup-status">
        <div className={`rounded-xl border p-4 ${settings.enabled ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
          <div className="text-[11px] uppercase tracking-wide text-slate-500">Planification</div>
          <div className="font-semibold text-slate-800" data-testid="backup-status-enabled">{settings.enabled ? "Activée" : "Désactivée"}</div>
          <div className="text-xs text-slate-600">Prochaine : <span data-testid="backup-next-run">{settings.enabled ? fmt(settings.next_run_at) : "—"}</span></div>
        </div>
        <div className={`rounded-xl border p-4 ${settings.last_status ? (ok ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200") : "bg-slate-50 border-slate-200"}`}>
          <div className="text-[11px] uppercase tracking-wide text-slate-500">Dernière sauvegarde</div>
          <div className="font-semibold text-slate-800" data-testid="backup-last-status">{settings.running ? `En cours — ${settings.running_step || "préparation"}` : settings.last_status ? (ok ? "Réussie" : "Échec") : "Aucune"}</div>
          <div className="text-xs text-slate-600">{fmt(settings.last_run_at)}</div>
        </div>
        <div className="rounded-xl border p-4 bg-white flex flex-col gap-2">
          <button onClick={runNow} disabled={busy || settings.running || !settings.password_set || !settings.drive_configured}
            className="bg-indigo-600 text-white px-3 py-2 rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-indigo-700 disabled:opacity-50" data-testid="backup-run-now">
            {settings.running ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudUpload className="w-4 h-4" />} {settings.running ? "Sauvegarde en cours…" : "Sauvegarder maintenant"}
          </button>
          {drive?.folder_link && <a href={drive.folder_link} target="_blank" rel="noreferrer" className="text-xs text-indigo-700 hover:underline flex items-center justify-center gap-1" data-testid="backup-drive-link"><ExternalLink className="w-3 h-3" /> Ouvrir le dossier Drive</a>}
          {!settings.drive_configured && <div className="text-xs text-rose-700">Google Drive non configuré (compte de service).</div>}
          {!settings.password_set && <div className="text-xs text-amber-700 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Définissez un mot de passe ci-dessous pour activer.</div>}
        </div>
      </div>

      {settings.offline_due && settings.enabled && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 flex flex-wrap items-center gap-3" data-testid="backup-offline-banner">
          <HardDrive className="w-5 h-5 text-amber-700" />
          <div className="flex-1 min-w-[14rem] text-sm text-amber-900"><b>Copie hors ligne mensuelle à faire</b> — téléchargez la dernière sauvegarde et copiez-la sur votre disque externe, puis confirmez.{settings.offline_last_done_at && <span className="block text-xs text-amber-700">Dernière copie : {fmt(settings.offline_last_done_at)}</span>}</div>
          <button onClick={downloadLatest} disabled={!latest} className="text-sm px-3 py-1.5 rounded-lg border border-amber-400 bg-white hover:bg-amber-100 disabled:opacity-50" data-testid="backup-offline-download">Télécharger la dernière</button>
          <button onClick={offlineDone} className="text-sm px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700" data-testid="backup-offline-done">J'ai fait ma copie</button>
        </div>
      )}
      <BackupScheduleForm settings={settings} onSaved={(s) => { setSettings(s); toast.success("Réglages de sauvegarde enregistrés"); }} />
      <BackupAlertsForm settings={settings} onSaved={(s) => setSettings(s)} />
      <BackupDriveList drive={drive} onRefresh={() => load(true)} onRestored={() => load(true)} />
      <DossierBackup settings={settings} onChanged={() => load(false)} />
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Historique</h3>
        <button onClick={() => load(true)} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="backup-refresh"><RefreshCw className="w-3 h-3" /> Actualiser</button>
      </div>
      <BackupRuns runs={runs} />
      <LegacyJsonBackup />
    </div>
  );
}
